// ─── Sport definitions ────────────────────────────────────────────────────

export interface SportDef {
  id: string;              // internal id
  nameKey: string;         // i18n key
  icon: string;            // emoji
  theSportsDbSport: string; // TheSportsDB sport name used in API calls
}

export const SPORTS: SportDef[] = [
  { id: 'soccer',           nameKey: 'sports.soccer',          icon: '⚽', theSportsDbSport: 'Soccer' },
  { id: 'basketball',       nameKey: 'sports.basketball',      icon: '🏀', theSportsDbSport: 'Basketball' },
  { id: 'tennis',           nameKey: 'sports.tennis',          icon: '🎾', theSportsDbSport: 'Tennis' },
  { id: 'americanfootball', nameKey: 'sports.americanFootball', icon: '🏈', theSportsDbSport: 'American Football' },
];

// ─── League definitions ───────────────────────────────────────────────────
// NOTE: idLeague values are TheSportsDB league IDs. Verified against
// schedule/next/league API endpoint. DO NOT change without re-verifying.

export interface LeagueDef {
  id: string;           // TheSportsDB idLeague
  sport: string;        // sport id above
  nameKey: string;      // i18n key
  nameFallback: string; // English name (shown if i18n key missing)
  country: string;      // English country/region name (used in filter dropdown)
}

const L = (id: string, sport: string, nameKey: string, nameFallback: string, country: string): LeagueDef =>
  ({ id, sport, nameKey, nameFallback, country });

// ── Soccer: National ────────────────────────────────────────────────────
const BRASILEIRAO_A  = L('4351', 'soccer', 'sports.leagues.brasileiraoA',       'Brasileirão Série A',       'Brazil');
const BRASILEIRAO_B  = L('4404', 'soccer', 'sports.leagues.brasileiraoB',       'Brasileirão Série B',       'Brazil');
const COPA_DO_BRASIL = L('4725', 'soccer', 'sports.leagues.copaDoBrasil',       'Copa do Brasil',            'Brazil');
const PRIMERA_ARG    = L('4406', 'soccer', 'sports.leagues.primeraArgentina',   'Primera División',          'Argentina');
const MLS            = L('4346', 'soccer', 'sports.leagues.mls',                'MLS',                       'USA');
const SUPER_LIG      = L('4339', 'soccer', 'sports.leagues.superLig',           'Süper Lig',                 'Turkey');

// ── Soccer: Top European national (always shown — "universal" group) ────
const PREMIER_LEAGUE = L('4328', 'soccer', 'sports.leagues.premierLeague',      'Premier League',            'England');
const LA_LIGA        = L('4335', 'soccer', 'sports.leagues.laLiga',             'La Liga',                   'Spain');
const SERIE_A_IT     = L('4332', 'soccer', 'sports.leagues.serieAItalia',       'Serie A',                   'Italy');
const BUNDESLIGA     = L('4331', 'soccer', 'sports.leagues.bundesliga',         'Bundesliga',                'Germany');
const LIGUE_1        = L('4334', 'soccer', 'sports.leagues.ligue1',             'Ligue 1',                   'France');
const LIGA_PORTUGAL  = L('4344', 'soccer', 'sports.leagues.ligaPortugal',       'Liga Portugal',             'Portugal');

// ── Soccer: European club competitions (always shown) ───────────────────
const CHAMPIONS      = L('4480', 'soccer', 'sports.leagues.championsLeague',    'Champions League',          'Europe');
const EUROPA_LEAGUE  = L('4481', 'soccer', 'sports.leagues.europaLeague',       'Europa League',             'Europe');
const UEFA_NATIONS   = L('4490', 'soccer', 'sports.leagues.uefaNationsLeague',  'UEFA Nations League',       'Europe');
const EUROCOPA       = L('4502', 'soccer', 'sports.leagues.eurocopa',           'UEFA European Championship','Europe');

// ── Soccer: World (always shown) ─────────────────────────────────────────
const WORLD_CUP      = L('4429', 'soccer', 'sports.leagues.worldCup',           'FIFA World Cup',            'FIFA World Cup');
const WOMENS_WC      = L('4565', 'soccer', 'sports.leagues.womensWorldCup',     'FIFA Women\'s World Cup',   'FIFA World Cup');

// ── Soccer: South American continental ──────────────────────────────────
const LIBERTADORES   = L('4501', 'soccer', 'sports.leagues.libertadores',       'Copa Libertadores',         'South America');
const SUDAMERICANA   = L('4724', 'soccer', 'sports.leagues.sudamericana',       'Copa Sudamericana',         'South America');

// ── Soccer: North/Central American continental ───────────────────────────
const CONCACAF_CC    = L('4721', 'soccer', 'sports.leagues.concacafCC',         'CONCACAF Champions Cup',    'North America');
const GOLD_CUP       = L('4873', 'soccer', 'sports.leagues.goldCup',            'CONCACAF Gold Cup',         'North America');

// ── Basketball ───────────────────────────────────────────────────────────
const NBA            = L('4387', 'basketball', 'sports.leagues.nba',            'NBA',                       'USA');
const EUROLEAGUE     = L('4546', 'basketball', 'sports.leagues.euroleague',     'EuroLeague',                'Europe');
const FIBA_WC        = L('4549', 'basketball', 'sports.leagues.fibaWorldCup',   'FIBA World Cup',            'World');

// ── Tennis ───────────────────────────────────────────────────────────────
const ATP_TOUR       = L('4464', 'tennis', 'sports.leagues.atpTour',            'ATP Tour',                  'World');
const WTA_TOUR       = L('4517', 'tennis', 'sports.leagues.wtaTour',            'WTA Tour',                  'World');

// ── American Football ────────────────────────────────────────────────────
const NFL            = L('4391', 'americanfootball', 'sports.leagues.nfl',      'NFL',                       'USA');

// ─── Architecture ─────────────────────────────────────────────────────────
//
//  NATIONAL_LEAGUES[sport][countryCode]   → leagues of user's country
//  CONTINENTAL_LEAGUES[sport][continent]  → competitions of user's region
//  UNIVERSAL_LEAGUES[sport]               → globally famous (always shown)
//
//  getLeagueGroups() returns:
//    regional    = national (user's country)
//    continental = continental (user's region, if non-EU)
//    world       = [] (unused, kept for interface compat)
//    top         = universal (deduplicated against regional/continental)

// National leagues per country code
const NATIONAL_LEAGUES: Record<string, Record<string, LeagueDef[]>> = {
  soccer: {
    BR: [BRASILEIRAO_A, BRASILEIRAO_B, COPA_DO_BRASIL],
    AR: [PRIMERA_ARG],
    US: [MLS],
    TR: [SUPER_LIG],
  },
  basketball: {
    US: [NBA],
  },
  americanfootball: {
    US: [NFL],
  },
  tennis: {},
};

// Continental competitions per region
// EU users already have CL / Europa League / Nations / Euros in UNIVERSAL
const CONTINENTAL_LEAGUES: Record<string, Record<string, LeagueDef[]>> = {
  soccer: {
    SA: [LIBERTADORES, SUDAMERICANA],  // South America
    NA: [CONCACAF_CC, GOLD_CUP],       // North/Central America
  },
  basketball: {
    EU: [EUROLEAGUE],
  },
  americanfootball: {},
  tennis: {},
};

// Universal leagues: always shown for all users, regardless of region
const UNIVERSAL_LEAGUES: Record<string, LeagueDef[]> = {
  soccer: [
    PREMIER_LEAGUE, LA_LIGA, SERIE_A_IT, BUNDESLIGA, LIGUE_1, LIGA_PORTUGAL,
    CHAMPIONS, EUROPA_LEAGUE, UEFA_NATIONS, EUROCOPA,
    WORLD_CUP, WOMENS_WC,
  ],
  basketball: [NBA, FIBA_WC],
  americanfootball: [NFL],
  tennis: [ATP_TOUR, WTA_TOUR],
};

// ─── Continent detection ──────────────────────────────────────────────────

var EU_CODES: Record<string, number> = {
  GB:1, EN:1, ES:1, IT:1, DE:1, FR:1, PT:1, NL:1, TR:1, GR:1, PL:1,
  RO:1, UA:1, HR:1, RS:1, SE:1, DK:1, NO:1, AT:1, CH:1, HU:1, CZ:1, IE:1, SC:1,
  BE:1, RU:1,
};
var SA_CODES: Record<string, number> = {
  BR:1, AR:1, CL:1, CO:1, PE:1, UY:1, PY:1, BO:1, EC:1, VE:1,
};
var NA_CODES: Record<string, number> = {
  US:1, MX:1, CA:1,
};

function getContinent(countryCode: string): string {
  if (EU_CODES[countryCode]) return 'EU';
  if (SA_CODES[countryCode]) return 'SA';
  if (NA_CODES[countryCode]) return 'NA';
  return 'OTHER';
}

export interface LeagueGroups {
  regional: LeagueDef[];
  continental: LeagueDef[];
  world: LeagueDef[];
  top: LeagueDef[];
}

export function getLeagueGroups(sportId: string, countryCode: string): LeagueGroups {
  var continent = getContinent(countryCode);

  // National leagues of user's country
  var regional = (NATIONAL_LEAGUES[sportId] && NATIONAL_LEAGUES[sportId][countryCode]) || [];

  // Continental competitions for user's region (EU already has CL/EL/Nations/Euros in universal)
  var continental = (CONTINENTAL_LEAGUES[sportId] && CONTINENTAL_LEAGUES[sportId][continent]) || [];

  // Universal: globally famous — deduplicated against national + continental
  var usedIds: Record<string, boolean> = {};
  regional.forEach(function(l)    { usedIds[l.id] = true; });
  continental.forEach(function(l) { usedIds[l.id] = true; });
  var universalAll = UNIVERSAL_LEAGUES[sportId] || [];
  var top = universalAll.filter(function(l) { return !usedIds[l.id]; });

  return { regional: regional, continental: continental, world: [], top: top };
}

// ─── Region detection ─────────────────────────────────────────────────────

const LANG_TO_REGION: Record<string, string> = {
  'pt-BR': 'BR',
  'en':    'US',
  'es':    'ES',
  'fr':    'FR',
  'de':    'DE',
  'ar':    'SA',
  'it':    'IT',
  'tr':    'TR',
};

export function getRegionCode(): string {
  try {
    // 1. Explicit user choice
    var stored = localStorage.getItem('ott_tmdb_region');
    if (stored) return stored;

    // 2. Derive from BCP-47 locale tag — 'pt-BR' → 'BR', 'en-US' → 'US', etc.
    var lang = localStorage.getItem('i18nextLng') || localStorage.getItem('ott_lang') || '';
    if (!lang) {
      try { lang = navigator.language || ''; } catch (_) { lang = ''; }
    }
    var parts = lang.split('-');
    if (parts.length >= 2 && parts[1].length === 2) return parts[1].toUpperCase();
    return LANG_TO_REGION[lang] || LANG_TO_REGION[parts[0]] || 'US';
  } catch (e) {
    return 'US';
  }
}

// ─── Country name translations ───────────────────────────────────────────
// Key = English country name as returned by TheSportsDB strCountry field.

const COUNTRY_NAMES: Record<string, Record<string, string>> = {
  'Brazil':         { 'pt-BR': 'Brasil',            'es': 'Brasil',            'fr': 'Brésil',        'de': 'Brasilien',       'ar': 'البرازيل',                      'it': 'Brasile',          'tr': 'Brezilya',      'en': 'Brazil' },
  'England':        { 'pt-BR': 'Inglaterra',         'es': 'Inglaterra',        'fr': 'Angleterre',    'de': 'England',         'ar': 'إنجلترا',                       'it': 'Inghilterra',      'tr': 'İngiltere',     'en': 'England' },
  'Spain':          { 'pt-BR': 'Espanha',            'es': 'España',            'fr': 'Espagne',       'de': 'Spanien',         'ar': 'إسبانيا',                       'it': 'Spagna',           'tr': 'İspanya',       'en': 'Spain' },
  'Germany':        { 'pt-BR': 'Alemanha',           'es': 'Alemania',          'fr': 'Allemagne',     'de': 'Deutschland',     'ar': 'ألمانيا',                       'it': 'Germania',         'tr': 'Almanya',       'en': 'Germany' },
  'France':         { 'pt-BR': 'França',             'es': 'Francia',           'fr': 'France',        'de': 'Frankreich',      'ar': 'فرنسا',                         'it': 'Francia',          'tr': 'Fransa',        'en': 'France' },
  'Italy':          { 'pt-BR': 'Itália',             'es': 'Italia',            'fr': 'Italie',        'de': 'Italien',         'ar': 'إيطاليا',                       'it': 'Italia',           'tr': 'İtalya',        'en': 'Italy' },
  'Portugal':       { 'pt-BR': 'Portugal',           'es': 'Portugal',          'fr': 'Portugal',      'de': 'Portugal',        'ar': 'البرتغال',                      'it': 'Portogallo',       'tr': 'Portekiz',      'en': 'Portugal' },
  'Netherlands':    { 'pt-BR': 'Países Baixos',      'es': 'Países Bajos',      'fr': 'Pays-Bas',      'de': 'Niederlande',     'ar': 'هولندا',                        'it': 'Paesi Bassi',      'tr': 'Hollanda',      'en': 'Netherlands' },
  'Argentina':      { 'pt-BR': 'Argentina',          'es': 'Argentina',         'fr': 'Argentine',     'de': 'Argentinien',     'ar': 'الأرجنتين',                     'it': 'Argentina',        'tr': 'Arjantin',      'en': 'Argentina' },
  'USA':            { 'pt-BR': 'EUA',                'es': 'EE.UU.',            'fr': 'États-Unis',    'de': 'USA',             'ar': 'الولايات المتحدة',              'it': 'USA',              'tr': 'ABD',           'en': 'USA' },
  'Mexico':         { 'pt-BR': 'México',             'es': 'México',            'fr': 'Mexique',       'de': 'Mexiko',          'ar': 'المكسيك',                       'it': 'Messico',          'tr': 'Meksika',       'en': 'Mexico' },
  'Turkey':         { 'pt-BR': 'Turquia',            'es': 'Turquía',           'fr': 'Turquie',       'de': 'Türkei',          'ar': 'تركيا',                         'it': 'Turchia',          'tr': 'Türkiye',       'en': 'Turkey' },
  'Russia':         { 'pt-BR': 'Rússia',             'es': 'Rusia',             'fr': 'Russie',        'de': 'Russland',        'ar': 'روسيا',                         'it': 'Russia',           'tr': 'Rusya',         'en': 'Russia' },
  'Japan':          { 'pt-BR': 'Japão',              'es': 'Japón',             'fr': 'Japon',         'de': 'Japan',           'ar': 'اليابان',                       'it': 'Giappone',         'tr': 'Japonya',       'en': 'Japan' },
  'Australia':      { 'pt-BR': 'Austrália',          'es': 'Australia',         'fr': 'Australie',     'de': 'Australien',      'ar': 'أستراليا',                      'it': 'Australia',        'tr': 'Avustralya',    'en': 'Australia' },
  'Saudi Arabia':   { 'pt-BR': 'Arábia Saudita',     'es': 'Arabia Saudita',    'fr': 'Arabie Saoudite', 'de': 'Saudi-Arabien', 'ar': 'المملكة العربية السعودية',      'it': 'Arabia Saudita',   'tr': 'Suudi Arabistan','en': 'Saudi Arabia' },
  'Colombia':       { 'pt-BR': 'Colômbia',           'es': 'Colombia',          'fr': 'Colombie',      'de': 'Kolumbien',       'ar': 'كولومبيا',                      'it': 'Colombia',         'tr': 'Kolombiya',     'en': 'Colombia' },
  'Chile':          { 'pt-BR': 'Chile',              'es': 'Chile',             'fr': 'Chili',         'de': 'Chile',           'ar': 'تشيلي',                         'it': 'Cile',             'tr': 'Şili',          'en': 'Chile' },
  'Belgium':        { 'pt-BR': 'Bélgica',            'es': 'Bélgica',           'fr': 'Belgique',      'de': 'Belgien',         'ar': 'بلجيكا',                        'it': 'Belgio',           'tr': 'Belçika',       'en': 'Belgium' },
  'Scotland':       { 'pt-BR': 'Escócia',            'es': 'Escocia',           'fr': 'Écosse',        'de': 'Schottland',      'ar': 'اسكتلندا',                      'it': 'Scozia',           'tr': 'İskoçya',       'en': 'Scotland' },
  'Greece':         { 'pt-BR': 'Grécia',             'es': 'Grecia',            'fr': 'Grèce',         'de': 'Griechenland',    'ar': 'اليونان',                       'it': 'Grecia',           'tr': 'Yunanistan',    'en': 'Greece' },
  'Poland':         { 'pt-BR': 'Polônia',            'es': 'Polonia',           'fr': 'Pologne',       'de': 'Polen',           'ar': 'بولندا',                        'it': 'Polonia',          'tr': 'Polonya',       'en': 'Poland' },
  'Romania':        { 'pt-BR': 'Romênia',            'es': 'Rumanía',           'fr': 'Roumanie',      'de': 'Rumänien',        'ar': 'رومانيا',                       'it': 'Romania',          'tr': 'Romanya',       'en': 'Romania' },
  'Ukraine':        { 'pt-BR': 'Ucrânia',            'es': 'Ucrania',           'fr': 'Ukraine',       'de': 'Ukraine',         'ar': 'أوكرانيا',                      'it': 'Ucraina',          'tr': 'Ukrayna',       'en': 'Ukraine' },
  'Croatia':        { 'pt-BR': 'Croácia',            'es': 'Croacia',           'fr': 'Croatie',       'de': 'Kroatien',        'ar': 'كرواتيا',                       'it': 'Croazia',          'tr': 'Hırvatistan',   'en': 'Croatia' },
  'Serbia':         { 'pt-BR': 'Sérvia',             'es': 'Serbia',            'fr': 'Serbie',        'de': 'Serbien',         'ar': 'صربيا',                         'it': 'Serbia',           'tr': 'Sırbistan',     'en': 'Serbia' },
  'Sweden':         { 'pt-BR': 'Suécia',             'es': 'Suecia',            'fr': 'Suède',         'de': 'Schweden',        'ar': 'السويد',                        'it': 'Svezia',           'tr': 'İsveç',         'en': 'Sweden' },
  'Denmark':        { 'pt-BR': 'Dinamarca',          'es': 'Dinamarca',         'fr': 'Danemark',      'de': 'Dänemark',        'ar': 'الدنمارك',                      'it': 'Danimarca',        'tr': 'Danimarka',     'en': 'Denmark' },
  'Norway':         { 'pt-BR': 'Noruega',            'es': 'Noruega',           'fr': 'Norvège',       'de': 'Norwegen',        'ar': 'النرويج',                       'it': 'Norvegia',         'tr': 'Norveç',        'en': 'Norway' },
  'Austria':        { 'pt-BR': 'Áustria',            'es': 'Austria',           'fr': 'Autriche',      'de': 'Österreich',      'ar': 'النمسا',                        'it': 'Austria',          'tr': 'Avusturya',     'en': 'Austria' },
  'Switzerland':    { 'pt-BR': 'Suíça',              'es': 'Suiza',             'fr': 'Suisse',        'de': 'Schweiz',         'ar': 'سويسرا',                        'it': 'Svizzera',         'tr': 'İsviçre',       'en': 'Switzerland' },
  'Hungary':        { 'pt-BR': 'Hungria',            'es': 'Hungría',           'fr': 'Hongrie',       'de': 'Ungarn',          'ar': 'المجر',                         'it': 'Ungheria',         'tr': 'Macaristan',    'en': 'Hungary' },
  'Czech Republic': { 'pt-BR': 'República Tcheca',   'es': 'República Checa',   'fr': 'République Tchèque', 'de': 'Tschechien', 'ar': 'جمهورية التشيك',              'it': 'Repubblica Ceca',  'tr': 'Çek Cumhuriyeti', 'en': 'Czech Republic' },
  'Ireland':        { 'pt-BR': 'Irlanda',            'es': 'Irlanda',           'fr': 'Irlande',       'de': 'Irland',          'ar': 'أيرلندا',                       'it': 'Irlanda',          'tr': 'İrlanda',       'en': 'Ireland' },
  'South Korea':    { 'pt-BR': 'Coreia do Sul',      'es': 'Corea del Sur',     'fr': 'Corée du Sud',  'de': 'Südkorea',        'ar': 'كوريا الجنوبية',                'it': 'Corea del Sud',    'tr': 'Güney Kore',    'en': 'South Korea' },
  'China':          { 'pt-BR': 'China',              'es': 'China',             'fr': 'Chine',         'de': 'China',           'ar': 'الصين',                         'it': 'Cina',             'tr': 'Çin',           'en': 'China' },
  'India':          { 'pt-BR': 'Índia',              'es': 'India',             'fr': 'Inde',          'de': 'Indien',          'ar': 'الهند',                         'it': 'India',            'tr': 'Hindistan',     'en': 'India' },
  'Egypt':          { 'pt-BR': 'Egito',              'es': 'Egipto',            'fr': 'Égypte',        'de': 'Ägypten',         'ar': 'مصر',                           'it': 'Egitto',           'tr': 'Mısır',         'en': 'Egypt' },
  'Morocco':        { 'pt-BR': 'Marrocos',           'es': 'Marruecos',         'fr': 'Maroc',         'de': 'Marokko',         'ar': 'المغرب',                        'it': 'Marocco',          'tr': 'Fas',           'en': 'Morocco' },
  'Uruguay':        { 'pt-BR': 'Uruguai',            'es': 'Uruguay',           'fr': 'Uruguay',       'de': 'Uruguay',         'ar': 'أوروغواي',                      'it': 'Uruguay',          'tr': 'Uruguay',       'en': 'Uruguay' },
  'Peru':           { 'pt-BR': 'Peru',               'es': 'Perú',             'fr': 'Pérou',         'de': 'Peru',            'ar': 'بيرو',                          'it': 'Perù',             'tr': 'Peru',          'en': 'Peru' },
  'South America':  { 'pt-BR': 'América do Sul',     'es': 'América del Sur',   'fr': 'Amérique du Sud', 'de': 'Südamerika',   'ar': 'أمريكا الجنوبية',               'it': 'Sud America',      'tr': 'Güney Amerika', 'en': 'South America' },
  'North America':  { 'pt-BR': 'América do Norte',   'es': 'América del Norte', 'fr': 'Amérique du Nord', 'de': 'Nordamerika',  'ar': 'أمريكا الشمالية',               'it': 'Nord America',     'tr': 'Kuzey Amerika', 'en': 'North America' },
  'Europe':         { 'pt-BR': 'Europa',             'es': 'Europa',            'fr': 'Europe',        'de': 'Europa',          'ar': 'أوروبا',                        'it': 'Europa',           'tr': 'Avrupa',        'en': 'Europe' },
  'World':          { 'pt-BR': 'Mundo',              'es': 'Mundo',             'fr': 'Monde',         'de': 'Welt',            'ar': 'العالم',                        'it': 'Mondo',            'tr': 'Dünya',         'en': 'World' },
  'FIFA World Cup': { 'pt-BR': 'Copa do Mundo',      'es': 'Copa del Mundo',    'fr': 'Coupe du Monde', 'de': 'Weltmeisterschaft', 'ar': 'كأس العالم',                  'it': 'Coppa del Mondo',  'tr': 'Dünya Kupası', 'en': 'FIFA World Cup' },
};

export function getCountryName(apiCountry: string, locale: string): string {
  var translations = COUNTRY_NAMES[apiCountry];
  if (!translations) return apiCountry;
  return translations[locale] || translations[locale.split('-')[0]] || apiCountry;
}

// ─── ISO → TheSportsDB country name ──────────────────────────────────────
// Maps ISO 3166-1 alpha-2 country codes to the English name expected by
// TheSportsDB search_all_leagues.php?c=<name> query parameter.
// Overrides: GB→England (TSDB splits UK), US→USA.

const ISO_TO_COUNTRY_NAME: Record<string, string> = {
  AD: 'Andorra',
  AE: 'United Arab Emirates',
  AG: 'Antigua and Barbuda',
  AL: 'Albania',
  AR: 'Argentina',
  AT: 'Austria',
  AU: 'Australia',
  AZ: 'Azerbaijan',
  BA: 'Bosnia and Herzegovina',
  BB: 'Barbados',
  BE: 'Belgium',
  BF: 'Burkina Faso',
  BG: 'Bulgaria',
  BH: 'Bahrain',
  BM: 'Bermuda',
  BO: 'Bolivia',
  BR: 'Brazil',
  BS: 'Bahamas',
  BY: 'Belarus',
  BZ: 'Belize',
  CA: 'Canada',
  CD: 'Congo',
  CH: 'Switzerland',
  CI: "Ivory Coast",
  CL: 'Chile',
  CO: 'Colombia',
  CR: 'Costa Rica',
  CU: 'Cuba',
  CV: 'Cape Verde',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DE: 'Germany',
  DK: 'Denmark',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  DZ: 'Algeria',
  EC: 'Ecuador',
  EE: 'Estonia',
  EG: 'Egypt',
  ES: 'Spain',
  FI: 'Finland',
  FJ: 'Fiji',
  FR: 'France',
  GB: 'England',
  GF: 'French Guiana',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GP: 'Guadeloupe',
  GQ: 'Equatorial Guinea',
  GR: 'Greece',
  GT: 'Guatemala',
  GY: 'Guyana',
  HK: 'Hong Kong',
  HN: 'Honduras',
  HR: 'Croatia',
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IN: 'India',
  IQ: 'Iraq',
  IS: 'Iceland',
  IT: 'Italy',
  JM: 'Jamaica',
  JO: 'Jordan',
  JP: 'Japan',
  KE: 'Kenya',
  KR: 'South Korea',
  KW: 'Kuwait',
  LB: 'Lebanon',
  LC: 'Saint Lucia',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LV: 'Latvia',
  LY: 'Libya',
  MA: 'Morocco',
  MC: 'Monaco',
  MD: 'Moldova',
  MK: 'North Macedonia',
  ML: 'Mali',
  MQ: 'Martinique',
  MT: 'Malta',
  MU: 'Mauritius',
  MW: 'Malawi',
  MX: 'Mexico',
  MY: 'Malaysia',
  MZ: 'Mozambique',
  NE: 'Niger',
  NG: 'Nigeria',
  NI: 'Nicaragua',
  NL: 'Netherlands',
  NO: 'Norway',
  NP: 'Nepal',
  NZ: 'New Zealand',
  OM: 'Oman',
  PA: 'Panama',
  PE: 'Peru',
  PF: 'French Polynesia',
  PG: 'Papua New Guinea',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Poland',
  PS: 'Palestine',
  PT: 'Portugal',
  PY: 'Paraguay',
  QA: 'Qatar',
  RE: 'Reunion',
  RO: 'Romania',
  RS: 'Serbia',
  RU: 'Russia',
  SA: 'Saudi Arabia',
  SC: 'Seychelles',
  SD: 'Sudan',
  SE: 'Sweden',
  SG: 'Singapore',
  SI: 'Slovenia',
  SK: 'Slovakia',
  SL: 'Sierra Leone',
  SM: 'San Marino',
  SN: 'Senegal',
  SR: 'Suriname',
  SV: 'El Salvador',
  SZ: 'Eswatini',
  TC: 'Turks and Caicos Islands',
  TD: 'Chad',
  TH: 'Thailand',
  TT: 'Trinidad and Tobago',
  TR: 'Turkey',
  TZ: 'Tanzania',
  UA: 'Ukraine',
  UG: 'Uganda',
  US: 'USA',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VC: 'Saint Vincent and the Grenadines',
  VE: 'Venezuela',
  VG: 'British Virgin Islands',
  ZA: 'South Africa',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
};

/**
 * Returns the English country name accepted by TheSportsDB c= parameter,
 * given an ISO 3166-1 alpha-2 code. Returns '' if unknown (no query sent).
 */
export function getCountryNameForApi(isoCode: string): string {
  return ISO_TO_COUNTRY_NAME[isoCode.toUpperCase()] || '';
}
